import { AppRoutes } from "@/lib/routeResolver";

/**
 * Converts a string to a URL-safe slug segment by trimming,
 * stripping leading/trailing slashes, and replacing spaces with hyphens.
 */
export function slugifySegment(value: string): string {
  return value
    .trim()
    .replace(/^\/+|\/+$/g, "")
    .replace(/\s+/g, "-");
}

/**
 * Normalises an absolute URL: returns the pathname for same-origin URLs,
 * passes through external URLs, and falls back to a dynamic page route.
 */
export function normalizeAbsoluteUrl(url: string): string {
  const trimmedUrl = url?.trim();
  if (!trimmedUrl) return AppRoutes.home();

  if (trimmedUrl.startsWith("/")) return trimmedUrl;

  try {
    const parsedUrl = new URL(trimmedUrl);
    if (
      typeof window !== "undefined" &&
      parsedUrl.origin === window.location.origin
    ) {
      return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
    }
    return trimmedUrl;
  } catch {
    return AppRoutes.dynamicPage(slugifySegment(trimmedUrl));
  }
}

export function isExternalLink(href: string): boolean {
  return /^https?:\/\//i.test(href);
}

/**
 * Lower-cased file extension of a URL, without the dot ("pdf", "jpg"), or an
 * empty string when there is none.
 *
 * Query strings and hashes are stripped first, so a signed asset URL such as
 * `.../flyer.pdf?X-Amz-Signature=…` still reports `pdf`. Works for absolute and
 * relative URLs alike — unlike `new URL()`, which throws on a relative path.
 */
export function getUrlExtension(url?: string | null): string {
  const path = url?.trim().split(/[?#]/)[0] ?? "";
  return path.match(/\.([a-z0-9]+)$/i)?.[1].toLowerCase() ?? "";
}

/** True when a URL points at a PDF document. */
export function isPdfUrl(url?: string | null): boolean {
  return getUrlExtension(url) === "pdf";
}
